/*
 * CAPTURE-OWNED MODULE
 * WorkflowDesigner — visual canvas editor using React Flow.
 * Loads a workflow by ID, manages nodes/edges, handles drag-drop from palette,
 * save/publish, and node selection for config panel.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { captureAPI } from '../services/api';
import NodePalette from '../components/workflow/NodePalette';
import WorkflowToolbar from '../components/workflow/WorkflowToolbar';
import NodeConfigPanel from '../components/workflow/NodeConfigPanel';
import WorkflowSettingsPanel from '../components/workflow/WorkflowSettingsPanel';
import PathwayDSavePrompt from '../components/workflow/PathwayDSavePrompt';
import ExecutionPanel from '../components/workflow/ExecutionPanel';
import BaseNode from '../components/workflow/nodes/BaseNode';
import DecisionNode from '../components/workflow/nodes/DecisionNode';
import { useCanBuildWorkflows } from '../identity/useIsAdmin';
import { pathwayDPromptShouldFire } from '../components/workflow/pathwayDPrompt';
import { DestinationListsContext, destinationListsFrom, serverMessage } from '../components/workflow/destinationStatus';
import './WorkflowDesigner.css';

// ── Node type metadata (for new node defaults) ──
const NODE_DEFAULTS = {
  trigger_upload:       { category: 'trigger',    description: 'Start workflow when document is uploaded', handles: { inputs: 0, outputs: 1 } },
  trigger_cloud_source: { category: 'trigger',    description: 'Start workflow when a document arrives from a connected cloud folder', handles: { inputs: 0, outputs: 1 } },
  action_await_ocr:     { category: 'processing', description: 'Wait until the document text has been acquired (text layer or OCR)', handles: { inputs: 1, outputs: 1 } },
  // DEPRECATED alias — renamed to action_await_ocr. Kept so workflow rows not
  // yet migrated still render in the designer. Remove with the server-side alias.
  action_ocr:           { category: 'processing', description: 'Wait until the document text has been acquired (text layer or OCR)', handles: { inputs: 1, outputs: 1 } },
  action_split:         { category: 'processing', description: 'Detect document boundaries and split multi-page PDFs into individual documents', handles: { inputs: 1, outputs: 1 } },
  action_extract:       { category: 'processing', description: 'Extract structured data from document', handles: { inputs: 1, outputs: 1 } },
  action_classify:      { category: 'processing', description: 'Classify document type', handles: { inputs: 1, outputs: 1 } },
  decision:             { category: 'routing',    description: 'Branch workflow based on conditions', handles: { inputs: 1, outputs: 2 } },
  action_human_review:  { category: 'action',     description: 'Pause for human review and approval', handles: { inputs: 1, outputs: 1 } },
  action_set_field:     { category: 'action',     description: 'Set or override a field value', handles: { inputs: 1, outputs: 1 } },
  action_field_mapping: { category: 'action',     description: 'Rename field keys for destination', handles: { inputs: 1, outputs: 1 } },
  output_publish:       { category: 'output',     description: 'Publish document to a destination', handles: { inputs: 1, outputs: 0 } },
  output_archive:       { category: 'output',     description: 'Archive and retain document', handles: { inputs: 1, outputs: 0 } },
};

// ── Category colors for minimap ──
const CATEGORY_COLORS = {
  trigger: '#059669',
  processing: '#2563eb',
  routing: '#d97706',
  action: '#7c3aed',
  output: '#dc2626',
};

let idCounter = 0;
function nextNodeId() {
  idCounter += 1;
  return `node_${Date.now()}_${idCounter}`;
}

// ── Inner component (must be inside ReactFlowProvider) ──
function DesignerInner() {
  const { id } = useParams();
  // R-panel (2026-06-14): the workflow-settings panel + per-workflow Pathway D
  // toggle + the save-prompt enable action are for BUILDERS (admin||power_user),
  // not admin-only — a power_user configures what they build. (The tenant-default
  // AI-behavior card on CaptureSettingsPage stays admin-only, separately.) The
  // /workflows/:id/designer route is RequireBuilder-guarded, so treat loading
  // (undefined) as allowed to avoid flicker; the route guard is the real gate.
  const canBuild = useCanBuildWorkflows() !== false;
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Workflow state
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Canvas state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Selection state
  const [selectedNode, setSelectedNode] = useState(null);

  // Cluster 5 Slice 2: workflow-settings panel + Pathway D save prompt
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [pathwayDPrompt, setPathwayDPrompt] = useState(null); // null | { proceed: fn }

  // A request the server refused, said on the page rather than in a browser dialog.
  const [notice, setNotice] = useState(null);

  // What became of each destination a Publish node may point at, for the
  // canvas nodes (components/workflow/destinationStatus). null = not known.
  const [destinationLists, setDestinationLists] = useState(null);
  const loadDestinationLists = useCallback(() => {
    captureAPI.destinations.list({ includeInactive: true, includeDeleted: true })
      .then((res) => setDestinationLists(destinationListsFrom(res)))
      .catch(() => setDestinationLists(null));
  }, []);
  useEffect(() => { loadDestinationLists(); }, [loadDestinationLists]);

  // Palette state
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);

  // Starter templates (for empty-state prompt)
  const [starterTemplates, setStarterTemplates] = useState([]);

  // Drag-over state
  const [dragOver, setDragOver] = useState(false);

  // Execution state
  const [executionId, setExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [executionNodeLog, setExecutionNodeLog] = useState([]);
  const [executionError, setExecutionError] = useState(null);
  const [executionStartedAt, setExecutionStartedAt] = useState(null);
  const [executionCompletedAt, setExecutionCompletedAt] = useState(null);
  const [showExecPanel, setShowExecPanel] = useState(false);
  const pollRef = useRef(null);

  // Batch execution state
  const [batchProgress, setBatchProgress] = useState(null);
  const [batchResults, setBatchResults] = useState([]);

  // Register custom node types
  const nodeTypes = useMemo(() => ({
    trigger_upload: BaseNode,
    // REQUIRED, not cosmetic: react-flow falls back to its own default renderer
    // for an unregistered type, so a missing entry here costs the node its icon,
    // accent stripe, category colour and handle counts. The palette is
    // server-fed (GET /node-types); this canvas registry is not.
    trigger_cloud_source: BaseNode,
    action_await_ocr: BaseNode,
    action_ocr: BaseNode, // DEPRECATED alias — see NODE_DEFAULTS above
    action_split: BaseNode,
    action_extract: BaseNode,
    action_classify: BaseNode,
    decision: DecisionNode,
    action_human_review: BaseNode,
    action_set_field: BaseNode,
    action_field_mapping: BaseNode,
    output_publish: BaseNode,
    output_archive: BaseNode,
  }), []);

  // ── Load starter templates ──
  useEffect(() => {
    (async () => {
      try {
        const res = await captureAPI.workflows.getTemplates();
        if (res.ok) setStarterTemplates(res.templates || []);
      } catch (err) {
        console.error('[WorkflowDesigner] Failed to load templates:', err);
      }
    })();
  }, []);

  // ── Load workflow ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await captureAPI.workflows.get(id);
        if (cancelled) return;
        if (!res.ok) throw new Error(res.error || 'Failed to load workflow');

        const wf = res.workflow;
        setWorkflow(wf);

        const def = wf.definition || {};
        const loadedNodes = (def.nodes || []).map(n => ({
          ...n,
          data: {
            ...n.data,
            category: n.data?.category || NODE_DEFAULTS[n.type]?.category || 'action',
            description: n.data?.description || NODE_DEFAULTS[n.type]?.description || '',
            handles: n.data?.handles || NODE_DEFAULTS[n.type]?.handles || { inputs: 1, outputs: 1 },
          },
        }));
        setNodes(loadedNodes);

        // Apply decision edge labels to loaded edges
        const loadedEdges = (def.edges || []).map(e => {
          if (e.sourceHandle === 'yes' || e.sourceHandle === 'no') {
            return {
              ...e,
              type: e.type || 'smoothstep',
              label: e.label || (e.sourceHandle === 'yes' ? 'Yes' : 'No'),
              labelStyle: e.labelStyle || {
                fill: e.sourceHandle === 'yes' ? '#059669' : '#dc2626',
                fontWeight: 600,
                fontSize: 11,
              },
              labelBgStyle: e.labelBgStyle || { fill: 'white', fillOpacity: 0.9 },
              labelBgPadding: e.labelBgPadding || [4, 2],
              labelBgBorderRadius: e.labelBgBorderRadius || 3,
              style: e.style || { stroke: '#94a3b8', strokeWidth: 2 },
            };
          }
          return {
            ...e,
            type: e.type || 'smoothstep',
            style: e.style || { stroke: '#94a3b8', strokeWidth: 2 },
          };
        });
        setEdges(loadedEdges);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, setNodes, setEdges]);

  // Mark dirty on any change after initial load
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    if (!loading) setDirty(true);
  }, [onNodesChange, loading]);

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    if (!loading) setDirty(true);
  }, [onEdgesChange, loading]);

  // ── Connect edges ──
  const onConnect = useCallback((params) => {
    const isDecisionEdge = params.sourceHandle === 'yes' || params.sourceHandle === 'no';
    const edgeData = {
      ...params,
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#94a3b8', strokeWidth: 2 },
    };
    if (isDecisionEdge) {
      edgeData.label = params.sourceHandle === 'yes' ? 'Yes' : 'No';
      edgeData.labelStyle = {
        fill: params.sourceHandle === 'yes' ? '#059669' : '#dc2626',
        fontWeight: 600,
        fontSize: 11,
      };
      edgeData.labelBgStyle = { fill: 'white', fillOpacity: 0.9 };
      edgeData.labelBgPadding = [4, 2];
      edgeData.labelBgBorderRadius = 3;
    }
    setEdges((eds) => addEdge(edgeData, eds));
    setDirty(true);
  }, [setEdges]);

  // ── Validate connection (output → input only) ──
  const isValidConnection = useCallback((connection) => {
    return connection.source !== connection.target;
  }, []);

  // ── Node selection ──
  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // ── Update selected node from config panel ──
  const handleNodeUpdate = useCallback((nodeId, updates) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            label: updates.label !== undefined ? updates.label : n.data.label,
            config: updates.config !== undefined ? updates.config : n.data.config,
          },
        };
      })
    );
    setDirty(true);
    // Update selectedNode reference
    setSelectedNode(prev => {
      if (!prev || prev.id !== nodeId) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          label: updates.label !== undefined ? updates.label : prev.data.label,
          config: updates.config !== undefined ? updates.config : prev.data.config,
        },
      };
    });
  }, [setNodes]);

  // ── Delete node ──
  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setDirty(true);
  }, [setNodes, setEdges]);

  // ── Drag and drop from palette ──
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragOver(false);

      const raw = event.dataTransfer.getData('application/reactflow-type');
      if (!raw) return;

      let nodeType;
      try {
        nodeType = JSON.parse(raw);
      } catch {
        return;
      }

      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const defaults = NODE_DEFAULTS[nodeType.type] || {};
      const newNode = {
        id: nextNodeId(),
        type: nodeType.type,
        position,
        data: {
          label: nodeType.label || nodeType.type,
          category: nodeType.category || defaults.category || 'action',
          description: nodeType.description || defaults.description || '',
          handles: nodeType.handles || defaults.handles || { inputs: 1, outputs: 1 },
          config: {},
        },
      };

      setNodes((nds) => [...nds, newNode]);
      setDirty(true);
    },
    [reactFlowInstance, setNodes],
  );

  // ── Save ──
  // Actual persist. `extra` lets the Pathway D prompt's "Enable Claude AI
  // fallback" action flip pathway_d_enabled=true in the same save (admin-only).
  const doSave = useCallback(async (extra) => {
    setSaving(true);
    setNotice(null);
    const fallback = 'The workflow could not be saved. Try again.';
    try {
      const viewport = reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 };
      const definition = { nodes, edges, viewport };

      const res = await captureAPI.workflows.update(id, { definition, ...(extra || {}) });
      if (!res.ok) {
        setNotice(typeof res.error === 'string' && res.error.trim() ? res.error : fallback);
        return;
      }
      setWorkflow(res.workflow);
      setDirty(false);
    } catch (err) {
      // The server's sentence when it sent one (a Publish step whose
      // destination was deleted, say). Its code stays in the console.
      console.error('[WorkflowDesigner] Save failed:', err?.response?.data?.code || '', err);
      setNotice(serverMessage(err, fallback));
      // A refusal is often about a destination that has changed since the
      // page loaded; the canvas nodes should say so too.
      loadDestinationLists();
    } finally {
      setSaving(false);
    }
  }, [id, nodes, edges, reactFlowInstance, loadDestinationLists]);

  const handleSave = useCallback(async () => {
    if (!workflow) return;
    // Cluster 5 / A8 pt 6 (R4): if Pathway D is OFF and the workflow has no
    // match-capable extraction path, prompt before saving (a guard, not a
    // blocker — "Save as-is" proceeds unchanged). Detection is a pure scan of
    // the in-memory nodes; see components/workflow/pathwayDPrompt.
    if (pathwayDPromptShouldFire(workflow, nodes)) {
      setPathwayDPrompt({ open: true });
      return;
    }
    await doSave();
  }, [workflow, nodes, doSave]);

  // Per-workflow Pathway D toggle (workflow-settings panel). Dedicated update
  // call (mirrors handleNameChange) — does NOT widen handleSave's payload.
  // Rides the A2 gate (admin-only on published); the toggle is admin-only-visible.
  const handlePathwayDChange = useCallback(async (enabled) => {
    if (!workflow) return;
    setSaving(true);
    setNotice(null);
    try {
      const res = await captureAPI.workflows.update(id, { pathway_d_enabled: enabled });
      if (res.ok) setWorkflow(res.workflow);
    } catch (err) {
      console.error('[WorkflowDesigner] Pathway D update failed:', err);
      setNotice(serverMessage(err, 'The setting could not be changed. Try again.'));
    } finally {
      setSaving(false);
    }
  }, [workflow, id]);

  // Change 2: replace the workflow's department set (R5) via the same
  // workflows.update path; rides the canBuildWorkflows gate on PUT /:id.
  const handleDepartmentsChange = useCallback(async (departmentKeys) => {
    if (!workflow) return;
    setNotice(null);
    try {
      const res = await captureAPI.workflows.update(id, { department_keys: departmentKeys });
      if (res.ok) setWorkflow(res.workflow);
    } catch (err) {
      console.error('[WorkflowDesigner] Department update failed:', err);
      setNotice(serverMessage(err, 'The departments could not be changed. Try again.'));
    }
  }, [workflow, id]);

  // ── Name change ──
  const handleNameChange = useCallback(async (newName) => {
    if (!workflow) return;
    try {
      const res = await captureAPI.workflows.update(id, { name: newName });
      if (res.ok) setWorkflow(res.workflow);
    } catch (err) {
      console.error('[WorkflowDesigner] Rename failed:', err);
    }
  }, [workflow, id]);

  // ── Publish / unpublish ──
  const handlePublish = useCallback(async () => {
    try {
      const res = await captureAPI.workflows.publish(id);
      if (res.ok) setWorkflow(res.workflow);
    } catch (err) {
      console.error('[WorkflowDesigner] Publish failed:', err);
    }
  }, [id]);

  const handleUnpublish = useCallback(async () => {
    try {
      const res = await captureAPI.workflows.unpublish(id);
      if (res.ok) setWorkflow(res.workflow);
    } catch (err) {
      console.error('[WorkflowDesigner] Unpublish failed:', err);
    }
  }, [id]);

  // ── Run workflow ──
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // ── Single-document execution with polling ──
  const runSingleDoc = useCallback(async (documentId) => {
    setExecutionNodeLog([]);
    setExecutionError(null);
    setExecutionCompletedAt(null);
    setExecutionStatus('running');
    setExecutionStartedAt(new Date().toISOString());
    setShowExecPanel(true);
    stopPolling();

    console.log(`[WorkflowDesigner] Running workflow=${id} doc=${documentId}`);
    const res = await captureAPI.workflows.execute(id, documentId);

    if (!res.ok && !res.executionId) {
      setExecutionStatus('failed');
      setExecutionError(res.error || 'Failed to start execution');
      setExecutionCompletedAt(new Date().toISOString());
      return res;
    }

    const execId = res.executionId;
    setExecutionId(execId);

    // Start polling for real-time node log
    pollRef.current = setInterval(async () => {
      try {
        const pollRes = await captureAPI.workflows.getExecution(execId);
        if (!pollRes.ok) return;

        const exec = pollRes.execution;
        setExecutionStatus(exec.status);
        setExecutionNodeLog(exec.node_log || []);
        setExecutionError(exec.error_message || null);
        setExecutionStartedAt(exec.started_at);
        setExecutionCompletedAt(exec.completed_at || null);

        if (['completed', 'failed', 'cancelled', 'paused'].includes(exec.status)) {
          stopPolling();
        }
      } catch (pollErr) {
        console.error('[WorkflowDesigner] Poll error:', pollErr);
      }
    }, 2000);

    return res;
  }, [id, stopPolling]);

  // ── Batch execution — sequential, with progress tracking ──
  const runBatch = useCallback(async (documentIds) => {
    setExecutionNodeLog([]);
    setExecutionError(null);
    setExecutionCompletedAt(null);
    setExecutionStatus('running');
    setExecutionStartedAt(new Date().toISOString());
    setShowExecPanel(true);
    stopPolling();

    const progress = { total: documentIds.length, completed: 0, failed: 0 };
    setBatchProgress({ ...progress });
    setBatchResults([]);

    console.log(`[WorkflowDesigner] Batch executing workflow=${id} docs=${documentIds.length}`);

    const results = [];
    for (let i = 0; i < documentIds.length; i++) {
      const docId = documentIds[i];
      try {
        const res = await captureAPI.workflows.execute(id, docId);
        const entry = {
          documentId: docId,
          executionId: res.executionId || '',
          status: res.ok ? (res.status || 'completed') : 'failed',
          error: res.error || null,
        };
        results.push(entry);
        if (res.ok) progress.completed++; else progress.failed++;
      } catch (err) {
        results.push({
          documentId: docId,
          executionId: '',
          status: 'failed',
          error: err.message || 'Execution failed',
        });
        progress.failed++;
      }
      setBatchProgress({ ...progress });
      setBatchResults([...results]);
    }

    const finalStatus = progress.failed === 0 ? 'completed' : (progress.completed === 0 ? 'failed' : 'completed');
    setExecutionStatus(finalStatus);
    setExecutionCompletedAt(new Date().toISOString());
    setExecutionError(
      progress.failed > 0
        ? `${progress.failed} of ${progress.total} document(s) failed`
        : null
    );
  }, [id, stopPolling]);

  // ── handleRun receives array of documentIds from RunWorkflowDialog ──
  const handleRun = useCallback(async (documentIds) => {
    if (!workflow || !documentIds || documentIds.length === 0) return;
    try {
      if (documentIds.length === 1) {
        setBatchProgress(null);
        setBatchResults([]);
        await runSingleDoc(documentIds[0]);
      } else {
        await runBatch(documentIds);
      }
    } catch (err) {
      console.error('[WorkflowDesigner] Run failed:', err);
      setExecutionStatus('failed');
      setExecutionError(err.message || 'Failed to execute workflow');
      setExecutionCompletedAt(new Date().toISOString());
    }
  }, [workflow, runSingleDoc, runBatch]);

  // ── Derive node execution status map from nodeLog ──
  const nodeExecStatusMap = useMemo(() => {
    const map = {};
    if (!executionNodeLog || executionNodeLog.length === 0) return map;
    for (const entry of executionNodeLog) {
      map[entry.node_id] = entry.status;
    }
    return map;
  }, [executionNodeLog]);

  // ── Inject executionStatus into node data whenever nodeLog changes ──
  useEffect(() => {
    if (!executionStatus) return; // No execution in progress
    setNodes((nds) =>
      nds.map((n) => {
        const status = nodeExecStatusMap[n.id] || null;
        if (n.data.executionStatus === status) return n; // No change
        return {
          ...n,
          data: { ...n.data, executionStatus: status },
        };
      })
    );
    // Style edges based on target node execution status
    setEdges((eds) =>
      eds.map((e) => {
        const targetStatus = nodeExecStatusMap[e.target] || null;
        if (targetStatus === 'completed') {
          return { ...e, animated: false, style: { ...e.style, stroke: '#059669', strokeWidth: 2.5 } };
        }
        if (targetStatus === 'running') {
          return { ...e, animated: true, style: { ...e.style, stroke: '#2563eb', strokeWidth: 2.5 } };
        }
        if (targetStatus === 'failed') {
          return { ...e, animated: false, style: { ...e.style, stroke: '#dc2626', strokeWidth: 2.5 } };
        }
        return { ...e, animated: false, style: { ...e.style, stroke: '#94a3b8', strokeWidth: 2 } };
      })
    );
  }, [nodeExecStatusMap, executionStatus, setNodes, setEdges]);

  // ── Clear execution status from nodes when panel is closed ──
  const handleCloseExecPanel = useCallback(() => {
    setShowExecPanel(false);
    stopPolling();
    setExecutionId(null);
    setExecutionStatus(null);
    setExecutionNodeLog([]);
    setExecutionError(null);
    setBatchProgress(null);
    setBatchResults([]);
    // Clear execution status from all nodes
    setNodes((nds) =>
      nds.map((n) => {
        if (!n.data.executionStatus) return n;
        return { ...n, data: { ...n.data, executionStatus: null } };
      })
    );
    // Reset edge styles
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        animated: false,
        style: { ...e.style, stroke: '#94a3b8', strokeWidth: 2 },
      }))
    );
  }, [stopPolling, setNodes, setEdges]);

  // ── Trigger node warning ──
  const triggerCount = nodes.filter(n => n.type?.startsWith('trigger_')).length;

  // ── Keyboard shortcuts ──
  const onKeyDown = useCallback((event) => {
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNode) {
      // Don't delete if user is typing in an input
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') return;
      handleNodeDelete(selectedNode.id);
    }
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      handleSave();
    }
  }, [selectedNode, handleNodeDelete, handleSave]);

  // ── Minimap node color ──
  const minimapNodeColor = useCallback((node) => {
    const cat = node.data?.category || NODE_DEFAULTS[node.type]?.category || 'action';
    return CATEGORY_COLORS[cat] || '#8b5cf6';
  }, []);

  // ── Loading / error states ──
  if (loading) {
    return (
      <div className="wfd-container">
        <div className="wfd-loading">
          <div className="spinner" />
          Loading workflow...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wfd-container">
        <div className="wfd-error">
          <span>Failed to load workflow: {error}</span>
          <Link to="/workflows">← Back to Workflows</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wfd-container" onKeyDown={onKeyDown} tabIndex={-1}>
      <WorkflowToolbar
        workflow={workflow}
        saving={saving}
        dirty={dirty}
        running={executionStatus === 'running'}
        batchProgress={batchProgress}
        onSave={handleSave}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onNameChange={handleNameChange}
        onRun={handleRun}
        onOpenSettings={() => setShowSettingsPanel(true)}
      />

      {notice && (
        <div className="wfd-notice" role="alert">
          <span className="wfd-notice-text">{notice}</span>
          <button type="button" className="wfd-notice-dismiss" onClick={() => setNotice(null)} aria-label="Dismiss">
            &times;
          </button>
        </div>
      )}

      <DestinationListsContext.Provider value={destinationLists}>
      <div className="wfd-body">
        <NodePalette
          collapsed={paletteCollapsed}
          onToggleCollapse={() => setPaletteCollapsed(p => !p)}
        />

        <div
          className={`wfd-canvas ${dragOver ? 'drag-over' : ''}`}
          ref={reactFlowWrapper}
        >
          {triggerCount > 1 && (
            <div className="wfd-trigger-warning">
              ⚠️ Multiple trigger nodes detected — a workflow should have exactly one trigger.
            </div>
          )}

          {/* Empty-state template prompt */}
          {nodes.length === 0 && !loading && starterTemplates.length > 0 && (
            <div className="wfd-empty-state">
              <div className="wfd-empty-state-inner">
                <div className="wfd-empty-state-title">This workflow is empty</div>
                <div className="wfd-empty-state-subtitle">Drag nodes from the palette, or start from a template:</div>
                <div className="wfd-empty-state-tpl-grid">
                  {starterTemplates.map(tpl => (
                    <button
                      key={tpl.id}
                      className="wfd-empty-state-tpl-card"
                      onClick={() => {
                        const def = tpl.definition;
                        const loaded = (def.nodes || []).map(n => ({
                          ...n,
                          data: {
                            ...n.data,
                            category: n.data?.category || NODE_DEFAULTS[n.type]?.category || 'action',
                            description: n.data?.description || NODE_DEFAULTS[n.type]?.description || '',
                            handles: n.data?.handles || NODE_DEFAULTS[n.type]?.handles || { inputs: 1, outputs: 1 },
                          },
                        }));
                        setNodes(loaded);
                        setEdges(def.edges || []);
                        setDirty(true);
                      }}
                    >
                      <span className="wfd-empty-state-tpl-icon">{tpl.icon || '\u26A1'}</span>
                      <span className="wfd-empty-state-tpl-name">{tpl.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onInit={setReactFlowInstance}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{
              type: 'smoothstep',
              style: { stroke: '#94a3b8', strokeWidth: 2 },
              animated: false,
            }}
            fitView
            deleteKeyCode={['Delete', 'Backspace']}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant="dots" gap={20} size={1} color="#e2e2e0" />
            <MiniMap
              nodeColor={minimapNodeColor}
              nodeStrokeWidth={2}
              zoomable
              pannable
              style={{ height: 100, width: 150 }}
            />
            <Controls />
          </ReactFlow>
        </div>

        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            // The cloud-source picker greys channels already used by ANOTHER
            // workflow, which it cannot tell apart from this one without this.
            workflowId={workflow?.id}
            onUpdate={handleNodeUpdate}
            onDelete={handleNodeDelete}
            onClose={() => setSelectedNode(null)}
          />
        )}

        {showSettingsPanel && (
          <WorkflowSettingsPanel
            workflow={workflow}
            canEdit={canBuild}
            saving={saving}
            onChangePathwayD={handlePathwayDChange}
            onChangeDepartments={handleDepartmentsChange}
            onClose={() => setShowSettingsPanel(false)}
          />
        )}
      </div>
      </DestinationListsContext.Provider>

      <ExecutionPanel
        visible={showExecPanel}
        executionStatus={executionStatus}
        nodeLog={executionNodeLog}
        errorMessage={executionError}
        startedAt={executionStartedAt}
        completedAt={executionCompletedAt}
        batchProgress={batchProgress}
        batchResults={batchResults}
        onClose={handleCloseExecPanel}
      />

      <PathwayDSavePrompt
        open={!!pathwayDPrompt}
        canEdit={canBuild}
        onClose={() => setPathwayDPrompt(null)}
        onSaveAsIs={() => { setPathwayDPrompt(null); doSave(); }}
        onEnableFallback={() => { setPathwayDPrompt(null); doSave({ pathway_d_enabled: true }); }}
      />
    </div>
  );
}

// ── Outer wrapper with ReactFlowProvider ──
const WorkflowDesigner = () => {
  return (
    <ReactFlowProvider>
      <DesignerInner />
    </ReactFlowProvider>
  );
};

export default WorkflowDesigner;
